import { ReactNode, useId } from 'react';
import { clsx } from 'clsx';
import { useInitialReveal } from '../../hooks/useInitialReveal';
import type { Tone } from '../../types/Tone';
import EmptyStateAction, { type IEmptyStateAction } from './EmptyStateAction';
import { emptyStateActionVariant } from './emptyStateActionVariant';
import './EmptyState.scss';

export type { IEmptyStateAction } from './EmptyStateAction';

/** `card` — пустая карточка справа от списка, `panel` — узкая панель, `inline` — блок в ряду содержимого (вид пустой
 *  таблицы). */
export type EmptyStateSize = 'card' | 'panel' | 'inline';

export type EmptyStateTone = Extract<Tone, 'primary' | 'neutral'>;

export interface IEmptyStateProps {
    /** Значок раздела — в диске над заголовком (у `inline` — без диска). */
    icon?: ReactNode;
    /** Цвет диска: `primary` — пустое место, которое ждёт выбора или создания; `neutral` — служебная панель.
     *  @default primary */
    tone?: EmptyStateTone;
    /** Заголовок блока (`<h2>`, имя области). */
    title: ReactNode;
    /** Строка под заголовком: что здесь появится. */
    description?: ReactNode;
    /** Действия; первое — главное. */
    actions?: IEmptyStateAction[];
    /** Подсказка под действиями (сочетание клавиш — `Kbd`). */
    hint?: ReactNode;
    /** @default card */
    size?: EmptyStateSize;
    /** Своё содержимое под подсказкой. */
    children?: ReactNode;
    className?: string;
    /** @default empty-state */
    'data-testid'?: string;
}

/** Появление блока — прозрачностью, без сдвига (`EmptyState.scss`). */
const REVEAL_MS = 160;

/**
 * Пустое состояние области: значок раздела в диске, заголовок, строка о том, что здесь появится, действия и подсказка.
 * Область — `<section>` с именем по заголовку `<h2>`. Какое состояние показать (ничего не выбрано, раздел пуст),
 * решает контейнер; действия передаёт страница — по правам пользователя.
 */
const EmptyState = ({
    icon,
    tone = 'primary',
    title,
    description,
    actions,
    hint,
    size = 'card',
    children,
    className,
    'data-testid': testId = 'empty-state',
}: IEmptyStateProps) => {
    const titleId = useId();
    const revealing = useInitialReveal(true, REVEAL_MS);

    return (
        <section
            className={clsx(
                'rf-empty-state',
                `rf-empty-state--${size}`,
                `rf-empty-state--${tone}`,
                revealing && 'rf-empty-state--revealing',
                className,
            )}
            aria-labelledby={titleId}
            data-testid={testId}
        >
            {icon != null && (
                <div className="rf-empty-state__icon" aria-hidden>
                    {icon}
                </div>
            )}
            <h2 id={titleId} className="rf-empty-state__title">
                {title}
            </h2>
            {description != null && <p className="rf-empty-state__description">{description}</p>}
            {!!actions?.length && (
                <div className="rf-empty-state__actions">
                    {actions.map((action, index) => (
                        <EmptyStateAction
                            key={`${index}-${action.label}`}
                            action={action}
                            variant={emptyStateActionVariant(action, index)}
                        />
                    ))}
                </div>
            )}
            {hint != null && <p className="rf-empty-state__hint">{hint}</p>}
            {children}
        </section>
    );
};

export default EmptyState;
